'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/platform/ui/card';
import { Alert, AlertDescription } from '@/platform/ui/alert';
import { Button } from '@/platform/ui/button';
import { Loader as Loader2, CircleCheck as CheckCircle2, Circle as XCircle } from 'lucide-react';
import { authClient } from '@/platform/auth/auth-client';
import { useAuth } from '@/platform/auth/auth-context';
import { fetchWithAuth } from '@/lib/auth/fetch-with-auth';
import { AmbientBackground } from '@/platform/ui/ambient-background';

export default function VerifyPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { session } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const token = searchParams.get('token');
        const type = searchParams.get('type');

        if (!token || type !== 'email') {
          setStatus('error');
          setError('Invalid verification link');
          return;
        }

        const { data, error: verifyError } = await authClient.auth.verifyOtp({
          token_hash: token,
          type: 'email'
        });

        if (verifyError) {
          setStatus('error');
          setError(verifyError.message);
          return;
        }

        if (data.user) {
          const response = await fetchWithAuth('/api/auth/verify-email', session, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId: data.user.id
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update verification status');
          }

          const statusResponse = await fetchWithAuth('/api/onboarding/status', session);
          const onboardingStatus = await statusResponse.json();

          setStatus('success');
          setTimeout(() => {
            if (onboardingStatus.hasInvitation) {
              router.push('/dashboard');
            } else if (onboardingStatus.hasCompany) {
              router.push('/dashboard');
            } else {
              router.push('/onboarding/company-setup');
            }
          }, 2000);
        } else {
          setStatus('error');
          setError('Verification failed');
        }
      } catch (err: any) {
        console.error('Email verification error:', err);
        setStatus('error');
        setError(err.message || 'An error occurred during verification');
      }
    };

    verifyEmail();
  }, [searchParams, router]);

  return (
    <>
      <AmbientBackground />
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'verifying' && (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle2 className="h-16 w-16 text-green-500" />
            )}
            {status === 'error' && (
              <XCircle className="h-16 w-16 text-destructive" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === 'verifying' && 'Verifying Your Email'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          <CardDescription>
            {status === 'verifying' && 'Please wait while we verify your email address...'}
            {status === 'success' && 'Your email has been successfully verified. Redirecting to dashboard...'}
            {status === 'error' && 'There was a problem verifying your email address.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex flex-col gap-2">
                <Button onClick={() => router.push('/auth/login')} className="w-full">
                  Go to Login
                </Button>
                <Button
                  variant="outline"
                  onClick={() => router.push('/auth/resend-verification')}
                  className="w-full"
                >
                  Resend Verification Email
                </Button>
              </div>
            </div>
          )}
          {status === 'success' && (
            <p className="text-center text-sm text-muted-foreground">
              You can now access all features of the platform.
            </p>
          )}
        </CardContent>
      </Card>
      </div>
    </>
  );
}
